import React, { FC, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';

import {
  ACFooter,
  ACHeader,
  ACIcon,
  ACLoading,
  ACProgress,
  ACQuoteMobile,
  ACErrorBoundary,
  ACChatbot,
} from 'shared/components';
import logoIcon from 'shared/assets/icon/logo.svg';
import {
  useGetBranding,
  useModal,
  useReadLocalStorage,
  useUpdateTaxAuditApp,
  useWindowSize,
} from 'shared/hooks';
import { useGetTaxAuditApp } from 'shared/hooks/api/useGetTaxAuditApp';
import { RootState } from 'shared/store/store';
import { updateQuote } from 'shared/store/slices/taa.slice';
import { Quote } from 'shared/models';
import { NotificationModal } from 'shared/sections/modal/notificationModal';
import { useRestoreQuote } from 'shared/hooks/api/useRestoreQuote';
import { createSession, setSessionId } from 'shared/hooks/api/useSessionApi';
import { Divider } from '@mui/material';

const excludedPathList = [
  'summary',
  'review',
  'pay',
  'complete',
  'entity-selection',
  'business-entity',
];

const mobileQuoteExcludeList = ['/referral'];

interface ACLayoutProps {
  children?: React.ReactNode;
  isDisplayProgressBar?: boolean;
  isLogoClickable?: boolean;
}

export const ACLayout: FC<ACLayoutProps> = ({
  children,
  isDisplayProgressBar = true,
  isLogoClickable = true,
}) => {
  const [partnerId, setPartnerId] = useState<string | null>('');
  const windowsSize = useWindowSize();
  const { id } = useParams();
  const [isSessionReady, setIsSessionReady] = useState(!id);
  const { data: TaaData } = useGetTaxAuditApp(isSessionReady ? id : undefined);
  const updateTAAService = useUpdateTaxAuditApp(id);
  const { quote, apiError, isExpired } = useSelector((state: RootState) => state.TaaApplication);
  const storedQuote = useReadLocalStorage<Quote>('quote');
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const { data: brandingData } = useGetBranding(partnerId);
  const { mutateAsync: restoreQuote } = useRestoreQuote(id ?? '');

  const modal = useModal();

  const isEnableMobileQuote = excludedPathList.some(keyword => location.pathname.includes(keyword));
  const groupData = partnerId ? brandingData?.branding : TaaData?.branding;

  const handleGoBackHome = async () => {
    if (isLogoClickable) {
      await updateTAAService.mutateAsync(
        {
          stage: 'EntityType_Stage',
        },
        {
          onSuccess: () => {
            navigate(`/entity-selection/${id}`);
          },
        }
      );
    }
  };

  const handleRestoreQuote = async () => {
    const restoredApplication = await restoreQuote();
    const hasPrimaryEntity = Boolean(restoredApplication.policyData?.primaryEntity);
    const nextStage = hasPrimaryEntity ? 'Summary_Stage' : 'EntityType_Stage';
    const nextPath = hasPrimaryEntity ? `/summary/${id}` : `/entity-selection/${id}`;

    await updateTAAService.mutateAsync(
      {
        stage: nextStage,
      },
      {
        onSuccess: () => {
          navigate(nextPath);
          navigate(0);
        },
      }
    );
  };

  useEffect(() => {
    if (id) {
      const sessionIdParam = queryParams.get('sessionid');
      if (sessionIdParam) {
        setSessionId(sessionIdParam);
        setIsSessionReady(true);
      } else {
        setIsSessionReady(false);
        createSession(id).then(() => setIsSessionReady(true));
      }
    }
  }, [id]);

  useEffect(() => {
    // Check the payment status
    if (
      (!location.pathname.includes('referral') && TaaData?.status === 'Issued') ||
      (!location.pathname.includes('pay') &&
        !location.pathname.includes('referral') &&
        TaaData?.status === 'PaymentProcessing') ||
      (!location.pathname.includes('referral') &&
        TaaData?.stage === 'Complete_Stage' &&
        TaaData?.status === 'PaymentProcessing')
    ) {
      navigate(`/complete/${id}`);
    }

    if (TaaData?.entityGroup === 'IndividualGroup' && !quote) {
      dispatch(updateQuote(storedQuote));
    }

    // Store the partnerId to the state
    // in case of it request the branding data again moving to next screen (fix the transition issue)
    if (queryParams.get('partner')) {
      setPartnerId(queryParams.get('partner'));
    }
  }, [TaaData]);

  // Show expired modal
  useEffect(() => {
    if (isExpired) {
      modal.handleOpen();
    }
  }, [isExpired]);

  const isAbphillips =
    TaaData?.branding?.contactUsUrl === 'https://www.abphillips.com.au/get-in-touch' &&
    (location.pathname.includes('/referral') || location.pathname.includes('/complete'));

  return (
    <div className="flex flex-col justify-between bg-[#efeff5] text-[.9375rem] font-normal leading-[1.25rem] min-h-screen min-w-full">
      <ACHeader>
        <ACIcon
          imgSrc={groupData?.isGroup ? `${groupData?.logoPath}?w=400` : logoIcon}
          className={`h-10 ${isLogoClickable && 'hover:cursor-pointer'}`}
          onClick={handleGoBackHome}
        />
      </ACHeader>

      {isDisplayProgressBar && (
        <ACProgress value={TaaData?.progress || 18} bgColor={groupData?.colour} />
      )}

      {windowsSize.width < 1280 &&
        !isEnableMobileQuote &&
        (quote || TaaData?.quote) &&
        !mobileQuoteExcludeList.some(path => location.pathname.includes(path)) && (
          <ACQuoteMobile quote={quote || TaaData!.quote} />
        )}

      <div className="mb-auto px-3 xl:px-0 w-full xl:max-w-[1296px] mx-auto">
        {apiError !== null ? (
          <ACErrorBoundary error={apiError} isApiError={true} isExpired={isExpired} />
        ) : (
          <ErrorBoundary FallbackComponent={ACErrorBoundary}>
            {isSessionReady ? children || <Outlet /> : <ACLoading />}
          </ErrorBoundary>
        )}
      </div>

      <Divider sx={{ backgroundColor: '#B1B1B1' }} className="w-full xl:max-w-[1500px] mx-auto" />

      <ACFooter
        legals={groupData?.legals}
        privacyUrl={groupData?.privacyUrl}
        contactLink={groupData?.contactUsUrl}
        isGroup={groupData?.isGroup}
        customizedContents={
          isAbphillips ? (
            <>
              <p>
                We do not represent that AB Philip’s services or the products they discuss with you
                are right for you and we take no responsibility for these products and services. You
                need to make your own decision based on the information you receive.
              </p>
              <p>We may receive a commission or agreed fee from AB Philips for this referral.</p>
            </>
          ) : (
            ''
          )
        }
      />

      <ACChatbot taaData={TaaData} />

      <NotificationModal open={modal.open} onClose={handleRestoreQuote} />
    </div>
  );
};
